import React, {useEffect, useState, useCallback, useRef} from 'react'
import * as Sentry from '@sentry/react'
import * as signalR from '@microsoft/signalr'
import {getAuth} from '../modules/auth/core/AuthHelpers'
import {useAuth} from '../modules/auth/core/Auth'
import {
  PresenceContext,
  PresenceContextType,
  InterpreterPresence,
  SimplifiedPresencePayload,
  CallInfo,
} from './PresenceContext'

// Paginated API response interface
interface PaginatedInterpretersResponse {
  data: InterpreterPresence[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  availableCount: number
  onCallCount: number
  offlineCount: number
}

interface PresenceProviderProps {
  children: React.ReactNode
}

type NormalizedInterpreter = {
  id: string
  userId: string
  name: string
  email: string
  phone: string
  hasDeviceToken: boolean
  communicationTypes: string[]
  languages: {name: string; serviceTypes: string[]}[]
}

const normalizeInterpreter = (i: InterpreterPresence): NormalizedInterpreter => {
  const communicationTypes = Array.isArray(i.communicationTypes) ? [...i.communicationTypes].sort() : []
  const languages = Array.isArray(i.languages)
    ? i.languages
        .map((l) => ({
          name: l.name ?? '',
          serviceTypes: Array.isArray(l.serviceTypes) ? [...l.serviceTypes].sort() : [],
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    : []

  return {
    id: i.id ?? '',
    userId: i.userId ?? '',
    name: i.name ?? '',
    email: i.email ?? '',
    phone: i.phone ?? '',
    hasDeviceToken: Boolean(i.hasDeviceToken),
    communicationTypes,
    languages,
  }
}

// Compare fetched interpreters to previous, ignoring `status` to avoid clobbering real-time updates.
const isSameInterpreterData = (a: InterpreterPresence, b: InterpreterPresence): boolean => {
  const na = normalizeInterpreter(a)
  const nb = normalizeInterpreter(b)

  if (
    na.id !== nb.id ||
    na.userId !== nb.userId ||
    na.name !== nb.name ||
    na.email !== nb.email ||
    na.phone !== nb.phone ||
    na.hasDeviceToken !== nb.hasDeviceToken
  ) {
    return false
  }

  if (na.communicationTypes.length !== nb.communicationTypes.length) return false
  for (let idx = 0; idx < na.communicationTypes.length; idx++) {
    if (na.communicationTypes[idx] !== nb.communicationTypes[idx]) return false
  }

  if (na.languages.length !== nb.languages.length) return false
  for (let idx = 0; idx < na.languages.length; idx++) {
    const la = na.languages[idx]
    const lb = nb.languages[idx]
    if (la.name !== lb.name) return false
    if (la.serviceTypes.length !== lb.serviceTypes.length) return false
    for (let j = 0; j < la.serviceTypes.length; j++) {
      if (la.serviceTypes[j] !== lb.serviceTypes[j]) return false
    }
  }

  return true
}

const mergeInterpretersPreservingUnchanged = (
  prev: InterpreterPresence[],
  next: InterpreterPresence[]
): InterpreterPresence[] => {
  const prevByUserId = new Map<string, InterpreterPresence>()
  prev.forEach((p) => {
    if (p?.userId) prevByUserId.set(p.userId, p)
  })

  let anyChange = prev.length !== next.length

  const merged = next.map((n) => {
    const p = n?.userId ? prevByUserId.get(n.userId) : undefined
    if (!p) {
      anyChange = true
      return n
    }

    // If only "status" changed (or nothing changed), preserve the old object reference
    if (isSameInterpreterData(p, n)) {
      return p
    }

    anyChange = true
    // Preserve real-time status from previous state
    return {...n, status: p.status}
  })

  return anyChange ? merged : prev
}

export const PresenceProvider: React.FC<PresenceProviderProps> = ({children}) => {
  const {currentUser} = useAuth()
  const isSystemUser = currentUser?.result?.userType === 'SYSTEM'
  const shouldConnect = isSystemUser

  const [interpreters, setInterpreters] = useState<InterpreterPresence[]>([])
  const [liveCalls, setLiveCalls] = useState<any>([])
  const [callQueues, setCallQueues] = useState<any>([])
  const [presenceSnapshot, setPresenceSnapshot] = useState<SimplifiedPresencePayload>({
    available: [],
    onCall: [],
  })
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [realTimeAgentStatus, setRealTimeAgentStatus] = useState<{status: string} | null>(null)

  // Counts from API (used until all interpreters are loaded)
  const [apiCounts, setApiCounts] = useState<{
    availableCount: number
    onCallCount: number
    offlineCount: number
    totalCount: number
  }>({availableCount: 0, onCallCount: 0, offlineCount: 0, totalCount: 0})
  const [allInterpretersLoaded, setAllInterpretersLoaded] = useState(false)

  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isInitializedRef = useRef(false)

  const apiBaseUrl = process.env.REACT_APP_WEB_SOCKET_URL

  // Setup ping-pong handler (server sends ping, client responds with pong)
  const setupPingPongHandler = useCallback(() => {
    const connection = connectionRef.current
    if (!connection) {
      console.error('[PresenceContext] ❌ Cannot setup ping-pong handler: no connection object')
      return
    }

    // Remove existing ping handler if any
    connection.off('ping')

    // Listen for ping from server
    connection.on('ping', async () => {
      const timestamp = new Date().toISOString()
      try {
        const currentConnection = connectionRef.current
        if (currentConnection && currentConnection.state === signalR.HubConnectionState.Connected) {
          await currentConnection.invoke('Pong')
        }
      } catch (error) {
        console.error('[PresenceContext] 🏓 ❌ Error sending pong at', timestamp, ':', error)
        if (error instanceof Error) {
          console.error('[PresenceContext] 🏓 Pong error details:', {
            message: error.message,
            stack: error.stack,
          })
        }
      }
    })
  }, [connectionRef])

  // Update interpreter status based on presence snapshot
  const updateInterpreterStatuses = useCallback((snapshot: SimplifiedPresencePayload) => {
    setPresenceSnapshot(snapshot)

    setInterpreters((prev) => {
      const updated = prev.map((interpreter) => {
        if (snapshot.onCall.includes(interpreter.id)) {
          return {...interpreter, status: 'On Call' as const}
        } else if (snapshot.available.includes(interpreter.id)) {
          return {...interpreter, status: 'Available' as const}
        } else {
          return {...interpreter, status: 'Offline' as const}
        }
      })
      return updated
    })
  }, [])

  // Fetch all interpreters from the API with progressive loading
  // First page loads immediately, remaining pages load in background
  const fetchInterpreters = useCallback(async () => {
    try {
      const auth = getAuth()
      const token = auth?.token

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Helper function to fetch a single page
      const fetchPage = async (
        page: number,
        pageSize: number
      ): Promise<PaginatedInterpretersResponse> => {
        const interpretersUrl = `${apiBaseUrl}/api/realtime/presence/interpreters?page=${page}&pageSize=${pageSize}`

        const response = await fetch(interpretersUrl, {
          method: 'GET',
          headers,
        })

        if (!response.ok) {
          const responseText = await response.text()
          console.error('[PresenceContext] Error response body:', responseText.substring(0, 500))

          if (response.status === 401) {
            throw new Error('Authentication required. Please log in.')
          }
          throw new Error(`Failed to fetch interpreters: ${response.status} ${response.statusText}`)
        }

        const contentType = response.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          const responseText = await response.text()
          console.error('[PresenceContext] Response is not JSON. Content-Type:', contentType)
          console.error(
            '[PresenceContext] Response body (first 500 chars):',
            responseText.substring(0, 500)
          )
          throw new Error(`Expected JSON but received ${contentType || 'unknown content type'}`)
        }

        return await response.json()
      }

      // Reset loaded flag when starting fresh fetch
      setAllInterpretersLoaded(false)

      // Step 1: Fetch first page immediately (500 items)
      const firstPageData = await fetchPage(1, 500)

      // Store counts from API response (used until all pages are loaded)
      setApiCounts({
        availableCount: firstPageData.availableCount,
        onCallCount: firstPageData.onCallCount,
        offlineCount: firstPageData.offlineCount,
        totalCount: firstPageData.totalCount,
      })

      // Show first 500 interpreters to user immediately
      setInterpreters((prev) => mergeInterpretersPreservingUnchanged(prev, firstPageData.data))
      setError(null)

      // Step 2: Fetch remaining pages in background if there are more
      if (firstPageData.hasNextPage) {

        let allInterpreters = [...firstPageData.data]
        let currentPage = 2

        // Fetch remaining pages sequentially to avoid overwhelming the server
        while (currentPage <= firstPageData.totalPages) {
          try {
            const pageData = await fetchPage(currentPage, 500)

            // Append new interpreters to the list
            allInterpreters = [...allInterpreters, ...pageData.data]

            // Update state progressively so UI reflects new data
            setInterpreters((prev) => mergeInterpretersPreservingUnchanged(prev, allInterpreters))

            currentPage++
          } catch (pageError) {
            console.error(`[PresenceContext] Error fetching page ${currentPage}:`, pageError)
            // Continue with next page even if one fails
            currentPage++
          }
        }

        // All pages loaded - now use calculated counts from full interpreter list
        setAllInterpretersLoaded(true)
      } else {
        // Only one page - all loaded immediately
        setAllInterpretersLoaded(true)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch interpreters'
      setError(errorMessage)
      console.error('[PresenceContext] Error fetching interpreters:', err)
      if (err instanceof Error) {
        console.error('[PresenceContext] Error details:', {
          message: err.message,
          stack: err.stack,
        })
      }
    }
  }, [apiBaseUrl])

  // Fetch current presence snapshot
  const fetchSnapshot = useCallback(async () => {
    try {
      const auth = getAuth()
      const token = auth?.token

      const snapshotUrl = `${apiBaseUrl}/realtime/presence/snapshot`

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(snapshotUrl, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        const responseText = await response.text()
        console.error('[PresenceContext] Error response body:', responseText.substring(0, 500))
        throw new Error(`Failed to fetch snapshot: ${response.status} ${response.statusText}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text()
        console.error('[PresenceContext] Response is not JSON. Content-Type:', contentType)
        console.error(
          '[PresenceContext] Response body (first 500 chars):',
          responseText.substring(0, 500)
        )
        throw new Error(`Expected JSON but received ${contentType || 'unknown content type'}`)
      }

      const data: SimplifiedPresencePayload = await response.json()
      updateInterpreterStatuses(data)
      setError(null)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch snapshot'
      setError(errorMessage)
      console.error('[PresenceContext] Error fetching snapshot:', err)
      if (err instanceof Error) {
        console.error('[PresenceContext] Error details:', {
          message: err.message,
          stack: err.stack,
        })
      }
    }
  }, [apiBaseUrl, updateInterpreterStatuses])

  // Fetch call queues
  const fetchCallQueues = useCallback(async () => {
    try {
      const auth = getAuth()
      const token = auth?.token

      const callQueuesUrl = `${apiBaseUrl}/api/realtime/presence/call-queues`

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(callQueuesUrl, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        const responseText = await response.text()
        console.error('[PresenceContext] Error response body:', responseText.substring(0, 500))
        throw new Error(`Failed to fetch call queues: ${response.status} ${response.statusText}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text()
        console.error('[PresenceContext] Response is not JSON. Content-Type:', contentType)
        console.error(
          '[PresenceContext] Response body (first 500 chars):',
          responseText.substring(0, 500)
        )
        throw new Error(`Expected JSON but received ${contentType || 'unknown content type'}`)
      }

      const data: CallInfo[] = await response.json()

      setCallQueues(data) // Add this line
      setError(null) // Add this line
    } catch (err) {
      console.error('[PresenceContext] Error fetching call queues:', err)
      if (err instanceof Error) {
        console.error('[PresenceContext] Error details:', {
          message: err.message,
          stack: err.stack,
        })
      }
    }
  }, [apiBaseUrl])

  // Fetch live calls
  const fetchLiveCalls = useCallback(async () => {
    try {
      const auth = getAuth()
      const token = auth?.token

      const liveCallsUrl = `${apiBaseUrl}/api/realtime/presence/live-calls`

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(liveCallsUrl, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        const responseText = await response.text()
        console.error('[PresenceContext] Error response body:', responseText.substring(0, 500))
        throw new Error(`Failed to fetch live calls: ${response.status} ${response.statusText}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text()
        console.error('[PresenceContext] Response is not JSON. Content-Type:', contentType)
        console.error(
          '[PresenceContext] Response body (first 500 chars):',
          responseText.substring(0, 500)
        )
        throw new Error(`Expected JSON but received ${contentType || 'unknown content type'}`)
      }

      const data: CallInfo[] = await response.json()
      setLiveCalls(data) // Add this line
      setError(null) // Add this line
    } catch (err) {
      console.error('[PresenceContext] Error fetching live calls:', err)
      if (err instanceof Error) {
        console.error('[PresenceContext] Error details:', {
          message: err.message,
          stack: err.stack,
        })
      }
    }
  }, [apiBaseUrl])

  // Initialize SignalR connection
  const initializeSignalR = useCallback(() => {
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      return // Already connected
    }

    const auth = getAuth()
    const token = auth?.token || ''
    const hubUrl = `${apiBaseUrl}/presenceHub`

    if (!apiBaseUrl) {
      console.error('[PresenceContext] ❌ REACT_APP_WEB_SOCKET_URL is not defined!')
      setError('WebSocket URL not configured')
      return
    }

    if (!token) {
      // No token
    }

    // Build connection URL with token as query parameter to avoid credentials
    const connectionUrl = token ? `${hubUrl}?access_token=${encodeURIComponent(token)}` : hubUrl

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(connectionUrl, {
        skipNegotiation: false,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
        // Don't use accessTokenFactory as it causes credentials to be sent
        // Instead, pass token in URL query string
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0s, 2s, 10s, 30s, then 30s intervals
          const delay =
            retryContext.previousRetryCount === 0
              ? 0
              : retryContext.previousRetryCount === 1
              ? 2000
              : retryContext.previousRetryCount === 2
              ? 10000
                  : 30000
          
          return delay
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build()

    // Handle presence updates from socket
    connection.on('presence:update', (payload: SimplifiedPresencePayload) => {
      updateInterpreterStatuses(payload)
    })

    connection.on('interpreter:upsert', (incoming: InterpreterPresence) => {
      if (!incoming?.id) return
      setInterpreters((prev) => {
        const index = prev.findIndex((interpreter) => interpreter.id === incoming.id)
        if (index === -1) return [...prev, incoming]
        const next = prev.slice()
        next[index] = {...prev[index], ...incoming}
        return next
      })
    })

    connection.on('interpreter:removed', (payload: {id?: string}) => {
      if (!payload?.id) return
      setInterpreters((prev) => prev.filter((interpreter) => interpreter.id !== payload.id))
    })

    connection.on('call-queues:update', (callQueuesData: CallInfo[]) => {
      setCallQueues(callQueuesData)
    })

    connection.on('live-calls:update', (liveCallsData: CallInfo[]) => {
      setLiveCalls(liveCallsData)
    })

    // Listen for agent status changes broadcasted from backend
    connection.on('status:change', (payload: {status: string}) => {
      Sentry.addBreadcrumb({
        message: 'Agent status changed via SignalR',
        level: 'info',
        data: {
          status: payload.status,
          source: 'SignalR',
        },
      })
      setRealTimeAgentStatus(payload)
    })

    // Connection event handlers
    connection.onclose((error) => {
      setIsConnected(false)
      if (error) {
        console.error('[PresenceContext] SignalR connection closed with error:', error)
        setError('Connection lost. Attempting to reconnect...')
      }
    })

    connection.onreconnecting((error) => {
      setIsConnected(false)
    })

    connection.onreconnected((connectionId) => {
      setIsConnected(true)
      setError(null)
      // Setup ping-pong handler on reconnection
      setupPingPongHandler()
      // Refresh snapshot after reconnection (only for SYSTEM users)
      if (isSystemUser) {
        fetchSnapshot()
      }
    })

    connectionRef.current = connection

    // Setup ping-pong handler before starting connection
    setupPingPongHandler()

    // Also listen for any SignalR messages for debugging
    connection.onreconnecting(() => {
    })

    // Start connection
    connection
      .start()
      .then(() => {
        setIsConnected(true)
        setError(null)

        // Fetch initial snapshot after connection (only for SYSTEM users)
        if (isSystemUser) {
          fetchSnapshot()
        }
      })
      .catch((err) => {
        console.error('[PresenceContext] ❌ Error starting SignalR connection:', err)
        setIsConnected(false)
        const errorMessage = err instanceof Error ? err.message : 'Failed to connect to SignalR'
        setError(errorMessage)
        if (err instanceof Error) {
          console.error('[PresenceContext] Error details:', {
            message: err.message,
            stack: err.stack,
            hubUrl,
            hasToken: !!token,
            userType: currentUser?.result?.userType,
          })
        }
      })
  }, [
    apiBaseUrl,
    updateInterpreterStatuses,
    fetchSnapshot,
    setupPingPongHandler,
    isSystemUser,
    currentUser,
  ])


  // Initialize everything - for SYSTEM and INTERPRETER users
  useEffect(() => {
    // Only initialize if user is SYSTEM or INTERPRETER type
    if (!shouldConnect) {
      setIsLoading(false)
      return
    }

    if (isInitializedRef.current) {
      return
    }

    isInitializedRef.current = true

    const initialize = async () => {
      setIsLoading(true)
      try {
        if (isSystemUser) {
          // For SYSTEM users: fetch everything
          await Promise.all([fetchSnapshot(), fetchCallQueues(), fetchLiveCalls()])

          // Set loading to false so UI is responsive
          setIsLoading(false)

          // Start fetching interpreters (first page shows immediately, rest loads in background)
          fetchInterpreters()
        } else {
          // For INTERPRETER users: just set loading to false
          setIsLoading(false)
        }

        // Initialize SignalR connection for SYSTEM and INTERPRETER users (for ping-pong)
        initializeSignalR()
      } catch (err) {
        console.error('[PresenceContext] Error initializing presence context:', err)
        if (err instanceof Error) {
          console.error('[PresenceContext] Initialization error details:', {
            message: err.message,
            stack: err.stack,
          })
        }
        setIsLoading(false)
      }
    }

    initialize()

    // Cleanup on unmount or when user type changes
    return () => {
      const reconnectTimeout = reconnectTimeoutRef.current
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }
      const connection = connectionRef.current
      if (connection) {
        // Remove ping handler
        connection.off('ping')
        connection
          .stop()
          .then(() => {
          })
          .catch((error) => {
            console.error('[PresenceContext] ❌ Error stopping SignalR connection:', error)
          })
        connectionRef.current = null
      }
      // Reset initialization flag when user type changes
      isInitializedRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldConnect])

  // Handle page visibility changes and page close
  useEffect(() => {
    if (!shouldConnect) return

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden - server will continue sending pings, client will respond
      } else {
        // Tab is visible again - ensure ping-pong handler is setup
        const connection = connectionRef.current
        if (connection && connection.state === signalR.HubConnectionState.Connected) {
          // Ensure ping-pong handler is setup
          setupPingPongHandler()
        }
      }
    }

    // Handle browser close event - connection will be closed, server will detect missing pong after 30 seconds
    const handleBeforeUnload = () => {
      const connection = connectionRef.current
      if (connection) {
        // Remove ping handler to prevent any pending operations
        connection.off('ping')
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [shouldConnect, setupPingPongHandler])

  // Helper methods
  const getInterpreterById = useCallback(
    (id: string): InterpreterPresence | undefined => {
      const interpreter = interpreters.find((interpreter) => interpreter.id === id)
      return interpreter
    },
    [interpreters]
  )

  const getAvailableInterpreters = useCallback((): InterpreterPresence[] => {
    const available = interpreters.filter((interpreter) => interpreter.status === 'Available')
    return available
  }, [interpreters])

  const getOnCallInterpreters = useCallback((): InterpreterPresence[] => {
    const onCall = interpreters.filter((interpreter) => interpreter.status === 'On Call')
    return onCall
  }, [interpreters])

  const getOfflineInterpreters = useCallback((): InterpreterPresence[] => {
    const offline = interpreters.filter((interpreter) => interpreter.status === 'Offline')
    return offline
  }, [interpreters])

  const refreshInterpreters = useCallback(async () => {
    await fetchInterpreters()
  }, [fetchInterpreters])

  const refreshCallQueues = useCallback(async () => {
    await fetchCallQueues()
  }, [fetchCallQueues])

  const refreshLiveCalls = useCallback(async () => {
    await fetchLiveCalls()
  }, [fetchLiveCalls])

  const refreshSnapshot = useCallback(async () => {
    await fetchSnapshot()
  }, [fetchSnapshot])

  // Compute counts - use API counts until all pages are loaded, then calculate from full list
  const calculatedAvailableCount = presenceSnapshot.available?.length || 0
  const calculatedOnCallCount = presenceSnapshot.onCall?.length || 0
  const calculatedTotalCount = interpreters?.length || 0
  const calculatedOfflineCount = Math.max(
    0,
    calculatedTotalCount - calculatedAvailableCount - calculatedOnCallCount
  )

  // Use API counts if not all pages loaded, otherwise use calculated counts
  const availableCount = allInterpretersLoaded ? calculatedAvailableCount : apiCounts.availableCount
  const onCallCount = allInterpretersLoaded ? calculatedOnCallCount : apiCounts.onCallCount
  const offlineCount = allInterpretersLoaded ? calculatedOfflineCount : apiCounts.offlineCount
  const totalInterpretersCount = allInterpretersLoaded ? calculatedTotalCount : apiCounts.totalCount

  // For SYSTEM users: return full context
  const value: PresenceContextType = {
        interpreters,
        callQueues,
        liveCalls,
        presenceSnapshot,
        isConnected,
        isLoading,
        error,
        allInterpretersLoaded,
        realTimeAgentStatus,
        availableCount,
        onCallCount,
        offlineCount,
        totalInterpretersCount,
        getInterpreterById,
        getAvailableInterpreters,
        getOnCallInterpreters,
        getOfflineInterpreters,
        refreshInterpreters,
        refreshSnapshot,
        refreshCallQueues,
        refreshLiveCalls,
      }

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>
}
